import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import {
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  REPORT_CLASSIFICATION_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { ReportUserFilter } from "@/components/features/reports/ReportUserFilter";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  Badge,
  Card,
  CardContent,
  CurrencyWrapper,
  DataTable,
  DataTableColumnHeader,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { MatterStatusSelectItems } from "@/components/ui/FormFields/MatterStatusSelectItems";
import { useUser } from "@/components/UserProvider";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import {
  getUsersOptions,
  retrieveMatterSettingsQueryOptions,
} from "@/lib/data/react-query";
import { retrieveReportsMatterSettlementReportQueryOptions } from "@/lib/data/react-query/reports/reports-matter-settlement-report";
import {
  CASE_MATTER_STATUS_CATEGORY_OPTIONS,
  getMatterStatusCategoryLabel,
} from "@/lib/data/static/matterStatics";
import { MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS } from "@/lib/data/static/matters/settlementStatics";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import {
  type ReportsMatterSettlementCounter,
  type ReportsMatterSettlementDemand,
  type ReportsMatterSettlementOffer,
} from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import {
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import { formatReportCsvCurrencyValue } from "@/lib/utils/reports/report-csv";
import {
  buildMatterStatusValue,
  formatMatterStatusLabel,
  parseMatterStatusValue,
  type ReportFilterSelectOption,
} from "@/lib/utils/reports/report-filter-options";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  Clock,
  HandCoins,
  Handshake,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/settlements")({
  component: RouteComponent,
  validateSearch: z.object({
    dateRangeType: z
      .enum([
        "ALL_TIME",
        "PREVIOUS_7_DAYS",
        "PREVIOUS_30_DAYS",
        "PREVIOUS_90_DAYS",
        "YEAR_TO_DATE",
      ])
      .optional(),
    team: z.string().optional(),
    member: z.string().optional(),
    type: z.string().optional(),
    claimType: z.string().optional(),
    settlementType: z.string().optional(),
    accepted: z.string().optional(),
    acceptedStatus: z.string().optional(),
    matterStatus: z.string().optional(),
  }),
});

const DATE_RANGE_OPTIONS = [
  { value: "ALL_TIME", label: "All time" },
  { value: "PREVIOUS_7_DAYS", label: "Previous 7 days" },
  { value: "PREVIOUS_30_DAYS", label: "Previous 30 days" },
  { value: "PREVIOUS_90_DAYS", label: "Previous 90 days" },
  { value: "YEAR_TO_DATE", label: "Year to date" },
] as const;

type DateRangeType = (typeof DATE_RANGE_OPTIONS)[number]["value"];

type SettlementReportRow =
  | (ReportsMatterSettlementDemand & { settlementType: "Demand" })
  | (ReportsMatterSettlementOffer & { settlementType: "Offer" })
  | (ReportsMatterSettlementCounter & { settlementType: "Counter" });

const SETTLEMENT_TYPE_OPTIONS = [
  { value: "Demand", label: "Demand" },
  { value: "Offer", label: "Offer" },
  { value: "Counter", label: "Counter" },
] as const;

const ACCEPTED_OPTIONS = [
  { value: "Accepted", label: "Accepted" },
  { value: "Not Accepted", label: "Not accepted" },
] as const;

const ACCEPTED_STATUS_OPTIONS: ReportFilterSelectOption[] =
  MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS.map((status) => ({
    value: status,
    label: status,
  }));

const SETTLEMENT_REPORT_TABLE_NAME =
  tanstackTableNames.reportsMatterSettlementDemands;
const SETTLEMENT_REPORT_PRESET_COLUMN_IDS = {
  acceptedStatus: "Money Status",
  assignees: "Assignees",
  attorneyFee: "Attorney Fee",
  checksIn: "Checks In",
  counterAmount: "Counter Amount",
  dateSent: "Date Sent",
  demandAmount: "Demand Amount",
  expirationDate: "Expiration Date",
  matterStatus: "Matter Status",
  matterTitle: "Matter Title",
  matterType: "Incident Type",
  claimTypes: "Claim Types",
  offerAccepted: "Accepted",
  offerAmount: "Offer Amount",
  policyLimits: "Policy Limits",
  policyLimitPerIncident: "Policy Limit Per Incident",
  policyLimitPerPerson: "Policy Limit Per Person",
  policyType: "Policy Type",
  received: "Received",
  teams: "Teams",
  totalMedicalTreatmentBills: "Total Medical Treatment Bills",
  type: "Type",
} as const;
const SETTLEMENT_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(SETTLEMENT_REPORT_PRESET_COLUMN_IDS).map(([key, value]) => [
    value,
    key,
  ]),
) as Record<string, keyof typeof SETTLEMENT_REPORT_PRESET_COLUMN_IDS>;
const DATE_RANGE_VALUES = new Set(
  DATE_RANGE_OPTIONS.map((option) => option.value),
);

type SettlementReportPresetFilters = {
  accepted?: string[];
  acceptedStatus?: string[];
  assigneeUserIds?: string[];
  dateRangeType?: string;
  matterStatus?: string[];
  incidentTypeIds?: string[];
  settlementType?: string[];
  teamIds?: string[];
};

const parseSearchValues = (value?: string) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => {
  const filters = rawFilters as {
    claimTypeIds?: string[];
    matterTypeIds?: string[];
  } & SettlementReportPresetFilters;
  const dateRangeType = DATE_RANGE_VALUES.has(
    filters.dateRangeType as DateRangeType,
  )
    ? (filters.dateRangeType as DateRangeType)
    : "ALL_TIME";

  return {
    claimType: serializeSearchValues(
      normalizePresetStringArray(filters.claimTypeIds),
    ),
    accepted: serializeSearchValues(
      normalizePresetStringArray(filters.accepted),
    ),
    acceptedStatus: serializeSearchValues(
      normalizePresetStringArray(filters.acceptedStatus),
    ),
    dateRangeType,
    matterStatus: serializeSearchValues(
      normalizePresetStringArray(filters.matterStatus),
    ),
    member: serializeSearchValues(
      normalizePresetStringArray(filters.assigneeUserIds),
    ),
    settlementType: serializeSearchValues(
      normalizePresetStringArray(filters.settlementType),
    ),
    team: serializeSearchValues(normalizePresetStringArray(filters.teamIds)),
    type: serializeSearchValues(
      normalizePresetStringArray(
        filters.incidentTypeIds ?? filters.matterTypeIds,
      ),
    ),
  };
};

const formatDateValue = (value?: string | null) => {
  const formatted = formatDate(value ?? undefined);
  return formatted ?? "-";
};

type SettlementOfferExtras = {
  matterSettlementReportOfferStatus?: string;
  matterSettlementOfferStatus?: string;
  matterSettlementReportOfferDateSent?: string;
  matterSettlementReportOfferDateExpires?: string;
};

type SettlementCounterExtras = {
  matterSettlementCounterDateSent?: string;
  matterSettlementCounterStatus?: string;
};

const getOfferStatus = (row: SettlementReportRow) => {
  if (row.settlementType !== "Offer") return undefined;
  const offer = row as SettlementReportRow & SettlementOfferExtras;
  return (
    offer.matterSettlementReportOfferStatus ?? offer.matterSettlementOfferStatus
  );
};

const getOfferDateExpires = (row: SettlementReportRow) => {
  if (row.settlementType !== "Offer") return undefined;
  const offer = row as SettlementReportRow & SettlementOfferExtras;
  return offer.matterSettlementReportOfferDateExpires;
};

const getOfferDateSent = (row: SettlementReportRow) => {
  if (row.settlementType !== "Offer") return undefined;
  const offer = row as SettlementReportRow & SettlementOfferExtras;
  return offer.matterSettlementReportOfferDateSent;
};

const getCounterStatus = (row: SettlementReportRow) => {
  if (row.settlementType !== "Counter") return undefined;
  const counter = row as SettlementReportRow & SettlementCounterExtras;
  return (
    row.matterSettlementReportCounterStatus ??
    counter.matterSettlementCounterStatus
  );
};

const getCounterDateSent = (row: SettlementReportRow) => {
  if (row.settlementType !== "Counter") return undefined;
  const counter = row as SettlementReportRow & SettlementCounterExtras;
  return (
    row.matterSettlementReportCounterDateSent ??
    counter.matterSettlementCounterDateSent
  );
};

const getSettlementAccepted = (row: SettlementReportRow) => {
  if (row.settlementType === "Offer") {
    return row.matterSettlementReportOfferAccepted;
  }
  if (row.settlementType === "Counter") {
    return row.matterSettlementReportCounterAccepted;
  }
  return undefined;
};

const getSettlementAcceptedStatus = (row: SettlementReportRow) => {
  if (row.settlementType === "Offer") {
    return getOfferStatus(row);
  }
  if (row.settlementType === "Counter") {
    return getCounterStatus(row);
  }
  return undefined;
};

const getSettlementAmount = (row: SettlementReportRow) => {
  const value =
    row.settlementType === "Demand"
      ? row.matterSettlementReportDemandAmount
      : row.settlementType === "Offer"
        ? row.matterSettlementReportOfferAmount
        : row.matterSettlementReportCounterAmount;

  return typeof value === "number" ? value : undefined;
};

const getSettlementAttorneyFeeAmount = (row: SettlementReportRow) => {
  const value =
    row.settlementType === "Demand"
      ? row.matterSettlementReportDemandAttorneyFeeAmount
      : row.settlementType === "Offer"
        ? row.matterSettlementReportOfferAttorneyFeeAmount
        : row.matterSettlementReportCounterAttorneyFeeAmount;

  return typeof value === "number" ? value : undefined;
};

const getSettlementPolicyLimit = (row: SettlementReportRow) => {
  if (row.settlementType === "Demand") {
    return row.matterSettlementReportDemandPolicyLimit;
  }
  if (row.settlementType === "Offer") {
    return row.matterSettlementReportOfferPolicyLimit;
  }
  return row.matterSettlementReportCounterPolicyLimit;
};

const getSettlementDateSent = (row: SettlementReportRow) => {
  if (row.settlementType === "Demand") {
    return row.matterSettlementReportDemandDateSent;
  }
  if (row.settlementType === "Offer") {
    return getOfferDateSent(row);
  }
  return getCounterDateSent(row);
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { hasPagePermission } = useUser();
  const search = Route.useSearch();

  const [selectedTeams, setSelectedTeams] = useState<string[]>(() =>
    parseSearchValues(search.team),
  );
  const [selectedMembers, setSelectedMembers] = useState<string[]>(() =>
    parseSearchValues(search.member),
  );
  const selectedClaimTypeIds = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    parseSearchValues(search.type),
  );
  const selectedDateRange = search.dateRangeType ?? "ALL_TIME";
  const [selectedSettlementTypes, setSelectedSettlementTypes] = useState<
    string[]
  >(() => parseSearchValues(search.settlementType));
  const [selectedAccepted, setSelectedAccepted] = useState<string[]>(() =>
    parseSearchValues(search.accepted),
  );
  const [selectedAcceptedStatuses, setSelectedAcceptedStatuses] = useState<
    string[]
  >(() => parseSearchValues(search.acceptedStatus));
  const [selectedMatterStatuses, setSelectedMatterStatuses] = useState<
    string[]
  >(() => parseSearchValues(search.matterStatus));
  const [teamSelectValue, setTeamSelectValue] = useState<string | undefined>();
  const [typeSelectValue, setTypeSelectValue] = useState<string | undefined>();
  const [settlementTypeSelectValue, setSettlementTypeSelectValue] = useState<
    string | undefined
  >();
  const [acceptedSelectValue, setAcceptedSelectValue] = useState<
    string | undefined
  >();
  const [acceptedStatusSelectValue, setAcceptedStatusSelectValue] = useState<
    string | undefined
  >();
  const [matterStatusSelectValue, setMatterStatusSelectValue] = useState<
    string | undefined
  >();

  const addUniqueValue = (values: string[], value: string) =>
    values.includes(value) ? values : [...values, value];

  useEffect(() => {
    setSelectedTeams(parseSearchValues(search.team));
  }, [search.team]);
  useEffect(() => {
    setSelectedMembers(parseSearchValues(search.member));
  }, [search.member]);
  useEffect(() => {
    setSelectedTypes(parseSearchValues(search.type));
  }, [search.type]);
  useEffect(() => {
    setSelectedSettlementTypes(parseSearchValues(search.settlementType));
  }, [search.settlementType]);
  useEffect(() => {
    setSelectedAccepted(parseSearchValues(search.accepted));
  }, [search.accepted]);
  useEffect(() => {
    setSelectedAcceptedStatuses(parseSearchValues(search.acceptedStatus));
  }, [search.acceptedStatus]);
  useEffect(() => {
    setSelectedMatterStatuses(parseSearchValues(search.matterStatus));
  }, [search.matterStatus]);

  const canViewSettlementReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsSettlements,
  );
  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewSettlementReport,
  });
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    ...getUsersOptions(),
    enabled: canViewSettlementReport,
  });

  const teamOptions = useMemo(() => {
    const teams = matterSettingsData?.data.teams ?? [];
    const options = teams.map((team) => ({
      value: team.id,
      label: team.label,
    }));
    const existing = new Set(options.map((option) => option.value));
    selectedTeams.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [matterSettingsData?.data.teams, selectedTeams]);

  const typeOptions = useMemo(() => {
    const types = matterSettingsData?.data.incidentTypes ?? [];
    const options = types.map((type) => ({
      value: type.id,
      label: getIncidentTypeLabel(type),
    }));
    const existing = new Set(options.map((option) => option.value));
    selectedTypes.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [matterSettingsData?.data.incidentTypes, selectedTypes]);

  const userDirectory = useUserDirectory({
    enabled: canViewSettlementReport,
    selectedIds: selectedMembers,
  });

  const memberOptions = useMemo(() => {
    const users = userDirectory.users;
    const mappedUsers = users
      .map((user) => {
        const nameParts = [user.firstName.trim(), user.lastName.trim()].filter(
          Boolean,
        );
        const label = nameParts.join(" ");
        return {
          value: user.id,
          label: label || user.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const options = [...mappedUsers];
    const existing = new Set(options.map((option) => option.value));
    selectedMembers.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [selectedMembers, userDirectory.users]);

  const acceptedStatusOptions = useMemo(() => {
    const existing = new Set(
      ACCEPTED_STATUS_OPTIONS.map((option) => option.value),
    );
    const options: ReportFilterSelectOption[] = [...ACCEPTED_STATUS_OPTIONS];
    selectedAcceptedStatuses.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });
    return options;
  }, [selectedAcceptedStatuses]);

  const filterOptions = {
    dateRange: DATE_RANGE_OPTIONS,
    teams: teamOptions,
    members: memberOptions,
    types: typeOptions,
  };

  const reportFilters = useMemo(() => {
    return {
      dateRange: {
        preset: selectedDateRange,
      },
      filters: {
        accepted: selectedAccepted.map((value) => value === "Accepted"),
        assigneeUserIds: selectedMembers,
        itemTypes: selectedSettlementTypes.map((value) =>
          value.toUpperCase(),
        ) as Array<"COUNTER" | "DEMAND" | "OFFER">,
        matterStatusIds: selectedMatterStatuses,
        incidentTypeIds: selectedTypes,
        claimTypeIds: selectedClaimTypeIds,
        teamIds: selectedTeams,
      },
    };
  }, [
    selectedAccepted,
    selectedDateRange,
    selectedMembers,
    selectedMatterStatuses,
    selectedSettlementTypes,
    selectedTeams,
    selectedTypes,
    selectedClaimTypeIds,
  ]);

  const { data, isLoading, error } = useQuery({
    ...retrieveReportsMatterSettlementReportQueryOptions(reportFilters),
    enabled: canViewSettlementReport,
  });

  const report = data?.data;
  const metrics = report?.metrics;

  const trustCheckTotalsByMatter = useMemo(() => {
    const totals = new Map<string, number>();
    const trustChecks = report?.matterTrustChecks ?? [];
    trustChecks.forEach((trustCheck) => {
      const record = trustCheck;
      const type =
        record.matterSettlementReportTrustCheckType ??
        record.type ??
        record.matterSettlementTrustCheckType ??
        record.trustCheckType;
      if (type !== "Deposit") return;
      const matterIdValue =
        record.matterSettlementReportMatterId ??
        record.matterId ??
        record.matterTrustCheckMatterId;
      if (typeof matterIdValue !== "string" || !matterIdValue) return;
      const matterId = matterIdValue;
      const amountValue =
        record.matterSettlementReportTrustCheckAmount ??
        record.amount ??
        record.matterSettlementTrustCheckAmount ??
        record.trustCheckAmount;
      const amount =
        typeof amountValue === "number" ? amountValue : Number(amountValue);
      if (!Number.isFinite(amount)) return;
      totals.set(matterId, (totals.get(matterId) ?? 0) + amount);
    });
    return totals;
  }, [report?.matterTrustChecks]);

  const settlementRows = useMemo<SettlementReportRow[]>(() => {
    const counters = report?.matterSettlementCounters ?? [];
    const demands = report?.matterSettlementDemands ?? [];
    const offers = report?.matterSettlementOffers ?? [];
    return [
      ...demands.map((demand) => ({
        ...demand,
        settlementType: "Demand" as const,
      })),
      ...offers.map((offer) => ({
        ...offer,
        settlementType: "Offer" as const,
      })),
      ...counters.map((counter) => ({
        ...counter,
        settlementType: "Counter" as const,
      })),
    ];
  }, [report]);

  const matterStatusOptions = useMemo(() => {
    const statuses = (matterSettingsData?.data.statuses ?? []).filter(
      (status) =>
        CASE_MATTER_STATUS_CATEGORY_OPTIONS.some(
          (category) => category === status.categoryId,
        ),
    );
    const options: ReportFilterSelectOption[] = statuses.map((status) => ({
      group: getMatterStatusCategoryLabel(status.categoryId),
      value: status.id,
      label: formatMatterStatusLabel(status.label, status.category.label),
    }));
    const existing = new Set(options.map((option) => option.value));

    selectedMatterStatuses.forEach((id) => {
      if (!existing.has(id)) {
        options.push({ value: id, label: id });
      }
    });

    return options;
  }, [matterSettingsData?.data.statuses, selectedMatterStatuses]);

  const selectedMatterStatusValues = useMemo(
    () =>
      new Set(
        (matterSettingsData?.data.statuses ?? [])
          .filter((status) => selectedMatterStatuses.includes(status.id))
          .map((status) =>
            buildMatterStatusValue(status.label, status.category.label),
          ),
      ),
    [matterSettingsData?.data.statuses, selectedMatterStatuses],
  );

  const selectedMatterStatusLabels = useMemo(() => {
    const labels = new Set<string>();
    selectedMatterStatusValues.forEach((value) => {
      const { label } = parseMatterStatusValue(value);
      if (label) {
        labels.add(label);
      }
    });
    return labels;
  }, [selectedMatterStatusValues]);

  const resolveOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    value: string,
  ) => options.find((option) => option.value === value)?.label ?? value;

  const claimFilterChips = selectedClaimTypeIds.map((id) => ({
    key: `claimType:${id}`,
    label: `Claim type: ${(matterSettingsData?.data.claimTypes ?? []).find((claimType) => claimType.id === id)?.label ?? id}`,
    onRemove: () => {
      void navigate({
        search: (prev) => ({
          ...prev,
          claimType: serializeSearchValues(
            selectedClaimTypeIds.filter((value) => value !== id),
          ),
        }),
        replace: true,
      });
    },
  }));
  const appliedFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      onRemove: () => void;
    }> = [];

    if (selectedDateRange !== "ALL_TIME") {
      const label =
        DATE_RANGE_OPTIONS.find((option) => option.value === selectedDateRange)
          ?.label ?? selectedDateRange;
      chips.push({
        key: `dateRange:${selectedDateRange}`,
        label: `Date range: ${label}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              dateRangeType: "ALL_TIME",
            }),
            replace: true,
          });
        },
      });
    }

    selectedTeams.forEach((value) => {
      chips.push({
        key: `team:${value}`,
        label: `Team: ${resolveOptionLabel(teamOptions, value)}`,
        onRemove: () => {
          const next = selectedTeams.filter((item) => item !== value);
          setSelectedTeams(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              team: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMembers.forEach((value) => {
      chips.push({
        key: `member:${value}`,
        label: `Assignee: ${resolveOptionLabel(memberOptions, value)}`,
        onRemove: () => {
          const next = selectedMembers.filter((item) => item !== value);
          setSelectedMembers(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              member: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedTypes.forEach((value) => {
      chips.push({
        key: `type:${value}`,
        label: `Type: ${resolveOptionLabel(typeOptions, value)}`,
        onRemove: () => {
          const next = selectedTypes.filter((item) => item !== value);
          setSelectedTypes(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              type: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedSettlementTypes.forEach((value) => {
      chips.push({
        key: `settlementType:${value}`,
        label: `Settlement Type: ${resolveOptionLabel(
          SETTLEMENT_TYPE_OPTIONS,
          value,
        )}`,
        onRemove: () => {
          const next = selectedSettlementTypes.filter((item) => item !== value);
          setSelectedSettlementTypes(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              settlementType: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedAccepted.forEach((value) => {
      chips.push({
        key: `accepted:${value}`,
        label: `Accepted: ${resolveOptionLabel(ACCEPTED_OPTIONS, value)}`,
        onRemove: () => {
          const next = selectedAccepted.filter((item) => item !== value);
          setSelectedAccepted(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              accepted: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedAcceptedStatuses.forEach((value) => {
      chips.push({
        key: `acceptedStatus:${value}`,
        label: `Money Status: ${resolveOptionLabel(
          acceptedStatusOptions,
          value,
        )}`,
        onRemove: () => {
          const next = selectedAcceptedStatuses.filter(
            (item) => item !== value,
          );
          setSelectedAcceptedStatuses(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              acceptedStatus: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMatterStatuses.forEach((value) => {
      chips.push({
        key: `matterStatus:${value}`,
        label: `Matter Status: ${resolveOptionLabel(matterStatusOptions, value)}`,
        onRemove: () => {
          const next = selectedMatterStatuses.filter((item) => item !== value);
          setSelectedMatterStatuses(next);
          void navigate({
            search: (prev) => ({
              ...prev,
              matterStatus: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    return chips;
  }, [
    memberOptions,
    navigate,
    acceptedStatusOptions,
    matterStatusOptions,
    selectedAccepted,
    selectedAcceptedStatuses,
    selectedDateRange,
    selectedMatterStatuses,
    selectedMembers,
    selectedSettlementTypes,
    selectedTeams,
    selectedTypes,
    teamOptions,
    typeOptions,
  ]);

  const activeSettlementTypes = useMemo(
    () =>
      selectedSettlementTypes.length > 0
        ? selectedSettlementTypes
        : SETTLEMENT_TYPE_OPTIONS.map((option) => option.value),
    [selectedSettlementTypes],
  );

  const filteredSettlementRows = useMemo(() => {
    let rows =
      selectedSettlementTypes.length === 0
        ? settlementRows
        : settlementRows.filter((row) =>
            selectedSettlementTypes.includes(row.settlementType),
          );

    if (selectedAccepted.length > 0) {
      rows = rows.filter((row) => {
        if (row.settlementType === "Demand") return false;
        const accepted = getSettlementAccepted(row) ?? false;
        return selectedAccepted.some((value) =>
          value === "Accepted" ? accepted : !accepted,
        );
      });
    }

    if (selectedAcceptedStatuses.length > 0) {
      rows = rows.filter((row) => {
        if (row.settlementType === "Demand") return false;
        const resolvedStatus = getSettlementAcceptedStatus(row);
        if (!resolvedStatus) return false;
        return selectedAcceptedStatuses.includes(resolvedStatus);
      });
    }

    if (selectedMatterStatuses.length > 0) {
      rows = rows.filter((row) => {
        const label = row.matterSettlementReportMatterStatusLabel?.trim();
        if (!label) return false;
        const category = row.matterSettlementReportMatterStatusCategory?.trim();
        const value = buildMatterStatusValue(label, category);
        if (selectedMatterStatusValues.has(value)) {
          return true;
        }
        return !category && selectedMatterStatusLabels.has(label);
      });
    }

    return rows;
  }, [
    selectedSettlementTypes,
    settlementRows,
    selectedAccepted,
    selectedAcceptedStatuses,
    selectedMatterStatusLabels,
    selectedMatterStatusValues,
    selectedMatterStatuses,
  ]);

  const summaryCards: Array<{
    label: string;
    value?: number;
    fee?: number;
    icon: LucideIcon;
  }> = [
    {
      label: "Demands",
      value: metrics?.sumOfDemands,
      fee: metrics?.sumOfDemandsFees,
      icon: Handshake,
    },
    {
      label: "Offers",
      value: metrics?.sumOfOffers,
      fee: metrics?.sumOfOffersFees,
      icon: HandCoins,
    },
    {
      label: "Issues",
      value: metrics?.sumOfOffersWithIssues,
      fee: metrics?.sumOfOffersWithIssuesFees,
      icon: AlertCircle,
    },
    {
      label: "Pending",
      value: metrics?.sumOfPendingTrustChecks,
      fee: metrics?.sumOfPendingTrustChecksFees,
      icon: Clock,
    },
    {
      label: "Checks in",
      value: metrics?.sumOfTrustChecksIn,
      fee: metrics?.sumOfTrustChecksInFees,
      icon: Wallet,
    },
  ];

  const settlementColumns = useMemo<ColumnDef<SettlementReportRow>[]>(() => {
    const columns: ColumnDef<SettlementReportRow>[] = [
      {
        accessorKey: "matterSettlementReportMatterTitle",
        id: "Matter Title",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterSettlementReportMatterTitle ?? "-",
        enableHiding: false,
      },
      {
        accessorKey: "matterSettlementReportMatterStatusLabel",
        id: "Matter Status",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterSettlementReportMatterStatusLabel ?? "-",
      },
      {
        accessorKey: "matterSettlementReportIncidentType",
        id: "Incident Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterSettlementReportIncidentType ?? "-",
      },
      {
        accessorFn: (row) =>
          (row.claimTypes ?? []).map((claimType) => claimType.label).join(", "),
        id: "Claim Types",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <ClaimTypesCell claimTypes={row.original.claimTypes} />
        ),
      },
      {
        accessorFn: (row) => row.settlementType,
        id: "Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => row.original.settlementType,
      },
      {
        accessorKey: "matterSettlementReportInsuranceType",
        id: "Policy Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <Badge className="bg-muted text-foreground border-border hover:bg-muted/80 px-2.5 py-0.5 text-xs font-semibold">
            {row.original.matterSettlementReportInsuranceType ?? "-"}
          </Badge>
        ),
      },
      {
        accessorFn: (row) => getSettlementAttorneyFeeAmount(row),
        id: "Attorney Fee",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const feeAmount = getSettlementAttorneyFeeAmount(row.original);
          if (feeAmount === undefined) {
            return "-";
          }
          const amount = getSettlementAmount(row.original);
          const percent =
            amount && amount > 0
              ? Math.round((feeAmount / amount) * 100)
              : undefined;
          return (
            <span>
              <CurrencyWrapper value={feeAmount} as="span" showCents />
              {percent === undefined ? " (-)" : ` (${percent}%)`}
            </span>
          );
        },
      },
      {
        accessorFn: (row) => getSettlementAccepted(row),
        id: "Accepted",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          if (row.original.settlementType === "Demand") {
            return "-";
          }
          const accepted = getSettlementAccepted(row.original);
          return (
            <Badge
              className={
                accepted
                  ? "bg-foreground text-background px-2.5 py-0.5 text-xs font-semibold"
                  : "bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-semibold"
              }
            >
              {accepted ? "Yes" : "No"}
            </Badge>
          );
        },
      },
      {
        accessorFn: (row) => getSettlementAcceptedStatus(row),
        id: "Money Status",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          if (row.original.settlementType === "Demand") {
            return "-";
          }
          const status = getSettlementAcceptedStatus(row.original);
          return status && status.trim().length > 0 ? status : "-";
        },
      },
      {
        accessorKey: "matterSettlementReportMatterTeams",
        id: "Teams",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const teams = row.original.matterSettlementReportMatterTeams ?? [];
          const uniqueTeams = Array.from(
            new Set(
              teams
                .map((team) => team.trim())
                .filter((team): team is string => Boolean(team)),
            ),
          );
          return uniqueTeams.length ? uniqueTeams.join(", ") : "-";
        },
      },
      {
        accessorKey: "matterSettlementReportMatterAssigneeUserIds",
        id: "Assignees",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          if (isUsersLoading) {
            return (
              <Badge className="bg-muted text-muted h-4 w-12 animate-pulse text-transparent">
                Loading
              </Badge>
            );
          }
          const ids =
            row.original.matterSettlementReportMatterAssigneeUserIds ?? [];
          if (!ids.length) return "-";
          const uniqueIds = Array.from(new Set(ids.filter((id) => id.trim())));
          const users = usersData?.data ?? [];
          const labels = uniqueIds
            .map((id) => {
              const user = users.find((u) => u.id === id);
              if (!user) return "";
              const nameParts = [
                user.firstName.trim(),
                user.lastName.trim(),
              ].filter(Boolean);
              return nameParts.join(" ");
            })
            .filter((label) => label.length > 0);
          return <MultiValueCell value={labels} />;
        },
      },
      {
        accessorFn: (row) => getSettlementPolicyLimit(row),
        id: "Policy Limits",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const policyLimit = getSettlementPolicyLimit(row.original);
          return (
            <Badge
              className={
                policyLimit
                  ? "bg-foreground text-background px-2.5 py-0.5 text-xs font-semibold"
                  : "bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-semibold"
              }
            >
              {policyLimit ? "Yes" : "No"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "matterSettlementReportInsuranceLimitPerPerson",
        id: "Policy Limit Per Person",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper
            value={row.original.matterSettlementReportInsuranceLimitPerPerson}
          />
        ),
      },
      {
        accessorKey: "matterSettlementReportInsuranceLimitPerIncident",
        id: "Policy Limit Per Incident",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper
            value={row.original.matterSettlementReportInsuranceLimitPerIncident}
          />
        ),
      },
      {
        accessorKey: "matterSettlementReportMedicalBillTotal",
        id: "Total Medical Treatment Bills",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <CurrencyWrapper
            value={row.original.matterSettlementReportMedicalBillTotal}
          />
        ),
      },
      {
        accessorFn: (row) =>
          row.settlementType === "Demand"
            ? row.matterSettlementReportDemandDateExpires
            : getOfferDateExpires(row),
        id: "Expiration Date",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const value =
            row.original.settlementType === "Demand"
              ? row.original.matterSettlementReportDemandDateExpires
              : getOfferDateExpires(row.original);
          return formatDateValue(value);
        },
        sortingFn: "customDateTime",
      },
      {
        accessorFn: (row) =>
          row.settlementType === "Offer"
            ? row.matterSettlementReportOfferDateReceived
            : undefined,
        id: "Received",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const value =
            row.original.settlementType === "Offer"
              ? row.original.matterSettlementReportOfferDateReceived
              : undefined;
          return formatDateValue(value);
        },
        sortingFn: "customDateTime",
      },
      {
        accessorFn: (row) => getSettlementDateSent(row),
        id: "Date Sent",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const value = getSettlementDateSent(row.original);
          return formatDateValue(value);
        },
        sortingFn: "customDateTime",
      },
      {
        accessorFn: (row) =>
          trustCheckTotalsByMatter.get(
            row.matterSettlementReportMatterId ?? "",
          ),
        id: "Checks In",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const matterId = row.original.matterSettlementReportMatterId ?? "";
          const total = trustCheckTotalsByMatter.get(matterId);
          return total ? <CurrencyWrapper value={total} /> : "-";
        },
      },
    ];

    const amountInsertIndex = 4;

    if (activeSettlementTypes.includes("Demand")) {
      columns.splice(amountInsertIndex, 0, {
        accessorFn: (row) =>
          row.settlementType === "Demand"
            ? row.matterSettlementReportDemandAmount
            : undefined,
        id: "Demand Amount",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const amount =
            row.original.settlementType === "Demand"
              ? row.original.matterSettlementReportDemandAmount
              : undefined;
          return <CurrencyWrapper value={amount} />;
        },
      });
    }

    if (activeSettlementTypes.includes("Offer")) {
      columns.splice(
        amountInsertIndex + (activeSettlementTypes.includes("Demand") ? 1 : 0),
        0,
        {
          accessorFn: (row) =>
            row.settlementType === "Offer"
              ? row.matterSettlementReportOfferAmount
              : undefined,
          id: "Offer Amount",
          header: ({ column }) => <DataTableColumnHeader column={column} />,
          cell: ({ row }) => {
            const amount =
              row.original.settlementType === "Offer"
                ? row.original.matterSettlementReportOfferAmount
                : undefined;
            return <CurrencyWrapper value={amount} />;
          },
        },
      );
    }

    if (activeSettlementTypes.includes("Counter")) {
      columns.splice(
        amountInsertIndex +
          (activeSettlementTypes.includes("Demand") ? 1 : 0) +
          (activeSettlementTypes.includes("Offer") ? 1 : 0),
        0,
        {
          accessorFn: (row) =>
            row.settlementType === "Counter"
              ? row.matterSettlementReportCounterAmount
              : undefined,
          id: "Counter Amount",
          header: ({ column }) => <DataTableColumnHeader column={column} />,
          cell: ({ row }) => {
            const amount =
              row.original.settlementType === "Counter"
                ? row.original.matterSettlementReportCounterAmount
                : undefined;
            return <CurrencyWrapper value={amount} />;
          },
        },
      );
    }

    return columns;
  }, [
    activeSettlementTypes,
    isUsersLoading,
    trustCheckTotalsByMatter,
    usersData?.data,
  ]);
  const settlementPresetColumnKeysById = useMemo(
    () =>
      settlementColumns.reduce<Record<string, string>>((acc, column) => {
        if (typeof column.id !== "string") return acc;
        if (!(column.id in SETTLEMENT_REPORT_PRESET_COLUMN_KEYS)) {
          return acc;
        }
        acc[column.id] = SETTLEMENT_REPORT_PRESET_COLUMN_KEYS[column.id];
        return acc;
      }, {}),
    [settlementColumns],
  );

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: settlementColumns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      claimTypeIds: selectedClaimTypeIds,
      accepted: selectedAccepted,
      acceptedStatus: selectedAcceptedStatuses,
      dateRangeType: selectedDateRange,
      assigneeUserIds: selectedMembers,
      teamIds: selectedTeams,
      incidentTypeIds: selectedTypes,
      matterStatus: selectedMatterStatuses,
      settlementType: selectedSettlementTypes,
    },
    enabled: canViewSettlementReport,
    lockedVisibleColumnIds: [SETTLEMENT_REPORT_PRESET_COLUMN_IDS.matterTitle],
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: settlementPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.matterSettlement,
    tableName: SETTLEMENT_REPORT_TABLE_NAME,
  });
  const getSettlementAssigneeLabels = (ids?: string[]) => {
    const users = usersData?.data ?? [];
    const uniqueIds = Array.from(
      new Set((ids ?? []).filter((id) => id.trim())),
    );
    const labels = uniqueIds.map((id) => {
      const user = users.find((candidate) => candidate.id === id);
      if (!user) return id;

      const name = [user.firstName.trim(), user.lastName.trim()]
        .filter(Boolean)
        .join(" ");
      return name || id;
    });

    return labels.length > 0 ? labels.join(", ") : "-";
  };
  const getSettlementCsvValue = (
    row: SettlementReportRow,
    columnId: string,
  ) => {
    switch (columnId) {
      case "Claim Types":
        return (
          (row.claimTypes ?? [])
            .map((claimType) => claimType.label)
            .join(", ") || "-"
        );
      case "Matter Title":
        return row.matterSettlementReportMatterTitle ?? "-";
      case "Matter Status":
        return row.matterSettlementReportMatterStatusLabel ?? "-";
      case "Incident Type":
        return row.matterSettlementReportIncidentType ?? "-";
      case "Policy Type":
        return row.matterSettlementReportInsuranceType ?? "-";
      case "Attorney Fee": {
        const feeAmount = getSettlementAttorneyFeeAmount(row);
        if (feeAmount === undefined) {
          return "-";
        }

        const amount = getSettlementAmount(row);
        const percent =
          amount && amount > 0
            ? Math.round((feeAmount / amount) * 100)
            : undefined;
        return `${formatReportCsvCurrencyValue(feeAmount)} (${
          percent === undefined ? "-" : `${percent}%`
        })`;
      }
      case "Accepted":
        return row.settlementType === "Offer"
          ? row.matterSettlementReportOfferAccepted
            ? "Yes"
            : "No"
          : "-";
      case "Money Status": {
        if (row.settlementType !== "Offer") {
          return "-";
        }

        const status = getOfferStatus(row);
        return status && status.trim().length > 0 ? status : "-";
      }
      case "Teams": {
        const teams = Array.from(
          new Set(
            (row.matterSettlementReportMatterTeams ?? [])
              .map((team) => team.trim())
              .filter(Boolean),
          ),
        );
        return teams.length > 0 ? teams.join(", ") : "-";
      }
      case "Assignees":
        return getSettlementAssigneeLabels(
          row.matterSettlementReportMatterAssigneeUserIds,
        );
      case "Policy Limits": {
        const hasPolicyLimit =
          row.settlementType === "Demand"
            ? row.matterSettlementReportDemandPolicyLimit
            : row.matterSettlementReportOfferPolicyLimit;
        return hasPolicyLimit ? "Yes" : "No";
      }
      case "Policy Limit Per Person":
        return formatReportCsvCurrencyValue(
          row.matterSettlementReportInsuranceLimitPerPerson,
        );
      case "Policy Limit Per Incident":
        return formatReportCsvCurrencyValue(
          row.matterSettlementReportInsuranceLimitPerIncident,
        );
      case "Total Medical Treatment Bills":
        return formatReportCsvCurrencyValue(
          row.matterSettlementReportMedicalBillTotal,
        );
      case "Expiration Date": {
        const value =
          row.settlementType === "Demand"
            ? row.matterSettlementReportDemandDateExpires
            : getOfferDateExpires(row);
        return formatDateValue(value);
      }
      case "Received":
        return row.settlementType === "Offer"
          ? formatDateValue(row.matterSettlementReportOfferDateReceived)
          : "-";
      case "Date Sent": {
        const value =
          row.settlementType === "Demand"
            ? row.matterSettlementReportDemandDateSent
            : getOfferDateSent(row);
        return formatDateValue(value);
      }
      case "Checks In":
        return formatReportCsvCurrencyValue(
          trustCheckTotalsByMatter.get(
            row.matterSettlementReportMatterId ?? "",
          ),
        );
      case "Demand Amount":
        return row.settlementType === "Demand"
          ? formatReportCsvCurrencyValue(row.matterSettlementReportDemandAmount)
          : "-";
      case "Offer Amount":
        return row.settlementType === "Offer"
          ? formatReportCsvCurrencyValue(row.matterSettlementReportOfferAmount)
          : "-";
      case "Counter Amount":
        return "-";
      default:
        return "";
    }
  };

  if (!canViewSettlementReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Settlement report"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={[...appliedFilterChips, ...claimFilterChips].map(
            (chip) => toReportPresetPreviewFilter(chip.label),
          )}
          saveTitle="Save settlement report preset"
          selectId="settlement-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={[...appliedFilterChips, ...claimFilterChips].map(
          (chip) => ({
            ...chip,
            onRemove: () => {
              clearSelectedPreset();
              chip.onRemove();
            },
          }),
        )}
        onClearFilters={async () => {
          clearSelectedPreset();
          setSelectedTeams([]);
          setSelectedMembers([]);
          setSelectedTypes([]);
          setSelectedSettlementTypes([]);
          setSelectedAccepted([]);
          setSelectedAcceptedStatuses([]);
          setSelectedMatterStatuses([]);
          setTeamSelectValue(undefined);
          setTypeSelectValue(undefined);
          setSettlementTypeSelectValue(undefined);
          setAcceptedSelectValue(undefined);
          setAcceptedStatusSelectValue(undefined);
          setMatterStatusSelectValue(undefined);
          await navigate({
            search: () => ({
              dateRangeType: "ALL_TIME",
              team: undefined,
              member: undefined,
              type: undefined,
              claimType: undefined,
              settlementType: undefined,
              accepted: undefined,
              acceptedStatus: undefined,
              matterStatus: undefined,
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            value={selectedDateRange}
            onValueChange={async (value) => {
              const nextValue = value as DateRangeType;
              await navigate({
                search: (prev) => ({
                  ...prev,
                  dateRangeType: nextValue,
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.dateRange.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={teamSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTeams, value);
              setSelectedTeams(next);
              setTeamSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  team: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Teams" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.teams.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ReportUserFilter
            {...userDirectory.selectProps}
            items={memberOptions}
            onChange={async (value) => {
              const next = addUniqueValue(selectedMembers, value);
              setSelectedMembers(next);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  member: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          />
          <ClaimTypeFilter
            value={selectedClaimTypeIds}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(value),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={typeSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTypes, value);
              setSelectedTypes(next);
              setTypeSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  type: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Types" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.types.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={matterStatusSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedMatterStatuses, value);
              setSelectedMatterStatuses(next);
              setMatterStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  matterStatus: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Statuses" />
            </SelectTrigger>
            <SelectContent>
              <MatterStatusSelectItems items={matterStatusOptions} />
            </SelectContent>
          </Select>
          <Select
            value={settlementTypeSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedSettlementTypes, value);
              setSelectedSettlementTypes(next);
              setSettlementTypeSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  settlementType: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Settlement type" />
            </SelectTrigger>
            <SelectContent>
              {SETTLEMENT_TYPE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={acceptedSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedAccepted, value);
              setSelectedAccepted(next);
              setAcceptedSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  accepted: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Accepted" />
            </SelectTrigger>
            <SelectContent>
              {ACCEPTED_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={acceptedStatusSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedAcceptedStatuses, value);
              setSelectedAcceptedStatuses(next);
              setAcceptedStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  acceptedStatus: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Money status" />
            </SelectTrigger>
            <SelectContent>
              {acceptedStatusOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </ReportFilterBar>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label}>
              <CardContent className="space-y-2 pt-4">
                {isLoading ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                      <div className="bg-muted h-4 w-20 animate-pulse rounded" />
                    </div>
                    <div className="bg-muted h-7 w-32 animate-pulse rounded" />
                    <div className="bg-muted h-4 w-24 animate-pulse rounded" />
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full">
                        <Icon className="h-5 w-5" />
                      </div>
                      <Text variant="subHeading">{card.label}</Text>
                    </div>
                    <Text variant="title" className="text-2xl leading-8">
                      <CurrencyWrapper value={card.value} as="span" />
                    </Text>
                    <Text variant="subHeading" className="mt-0.5">
                      Fees: <CurrencyWrapper value={card.fee} as="span" />
                    </Text>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="space-y-4">
        <DataTable
          entityLabelPlural="settlements"
          columnIdAliases={REPORT_CLASSIFICATION_COLUMN_ALIASES}
          newColumnAnchors={REPORT_CLASSIFICATION_COLUMN_ANCHORS}
          key={tableRenderKey}
          hideSearchBar
          columns={settlementColumns}
          data={filteredSettlementRows}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          leftToolbarContent={
            <Text variant="body" className="text-lg font-semibold">
              Settlement details
            </Text>
          }
          onRowClick={async (row) => {
            const matterId = row.original.matterSettlementReportMatterId;
            if (!matterId) return;
            await navigate({
              to: "/matters/$matterId",
              params: { matterId },
            });
          }}
          tableName={SETTLEMENT_REPORT_TABLE_NAME}
          isLoading={isLoading}
          isError={Boolean(error)}
          noResultsMessage="No settlement records found."
          exportCsv={{
            fileName: "settlement-report.csv",
            getCellValue: getSettlementCsvValue,
          }}
        />
      </div>
    </ReportsPageLayout>
  );
}
